import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  TrendingUp, Briefcase, Users, DollarSign, LogOut, User, Calendar,
  Clock, FileText, CheckCircle, AlertCircle, PlusCircle, Search,
  Filter, Download, Upload, MessageSquare, Bell, Settings, BarChart3,
  PieChart, Activity, Target, Zap, Building2, X, Save, Sparkles
} from 'lucide-react';
import PageTitle from '../hooks/PageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SolicitorDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [financialSummary, setFinancialSummary] = useState(null);
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [
        financialRes,
        casesRes,
        clientsRes,
        timeRes,
        invoicesRes,
        tasksRes,
        docsRes
      ] = await Promise.all([
        axios.get(`${API_URL}/api/solicitor/financial-summary`, { withCredentials: true }),
        axios.get(`${API_URL}/api/solicitor/cases`, { withCredentials: true }),
        axios.get(`${API_URL}/api/solicitor/clients`, { withCredentials: true }),
        axios.get(`${API_URL}/api/solicitor/time-entries`, { withCredentials: true }),
        axios.get(`${API_URL}/api/solicitor/invoices`, { withCredentials: true }),
        axios.get(`${API_URL}/api/solicitor/tasks`, { withCredentials: true }),
        axios.get(`${API_URL}/api/solicitor/documents`, { withCredentials: true })
      ]);

      setFinancialSummary(financialRes.data);
      setCases(casesRes.data);
      setClients(clientsRes.data);
      setTimeEntries(timeRes.data);
      setInvoices(invoicesRes.data);
      setTasks(tasksRes.data);
      setDocuments(docsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'cases', label: 'Cases', icon: Briefcase },
    { id: 'clients', label: 'Clients', icon: Users },
    { id: 'time', label: 'Time Tracking', icon: Clock },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'tasks', label: 'Tasks', icon: CheckCircle },
    { id: 'documents', label: 'Documents', icon: Upload },
    { id: 'ai', label: 'AI Assistant', icon: Sparkles },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <PageTitle title="Solicitor Dashboard" />
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <div className="bg-gray-900 w-10 h-10 rounded-lg flex items-center justify-center">
                <Briefcase className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-serif text-gray-900">Solicitor Platform</h1>
                <p className="text-sm text-gray-600">{user?.name}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative">
                <Bell className="h-5 w-5 text-gray-600" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <Settings className="h-5 w-5 text-gray-600" />
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm"
              >
                <LogOut className="h-4 w-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="flex space-x-1 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-gray-900 text-gray-900 font-medium'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-sm">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <OverviewTab
            financialSummary={financialSummary}
            cases={cases}
            tasks={tasks}
            invoices={invoices}
          />
        )}
        {activeTab === 'cases' && <CasesTab cases={cases} clients={clients} refreshData={fetchAllData} user={user} />}
        {activeTab === 'clients' && <ClientsTab clients={clients} refreshData={fetchAllData} user={user} />}
        {activeTab === 'time' && <TimeTrackingTab timeEntries={timeEntries} cases={cases} refreshData={fetchAllData} user={user} />}
        {activeTab === 'invoices' && <InvoicesTab invoices={invoices} cases={cases} timeEntries={timeEntries} refreshData={fetchAllData} user={user} />}
        {activeTab === 'tasks' && <TasksTab tasks={tasks} cases={cases} refreshData={fetchAllData} user={user} />}
        {activeTab === 'documents' && <DocumentsTab documents={documents} cases={cases} refreshData={fetchAllData} user={user} />}
        {activeTab === 'ai' && <AIAssistant />}
      </div>
    </div>
  );
};

// ============= OVERVIEW TAB (unchanged) =============
const OverviewTab = ({ financialSummary, cases, tasks, invoices }) => {
  const stats = [
    {
      icon: DollarSign,
      label: 'Total Billed',
      value: `£${financialSummary?.total_billed?.toLocaleString() || 0}`,
      subtext: 'Gross revenue',
      color: 'bg-green-500'
    },
    {
      icon: TrendingUp,
      label: 'Your 70% Share',
      value: `£${financialSummary?.solicitor_share_70?.toLocaleString() || 0}`,
      subtext: 'Your earnings',
      color: 'bg-blue-500'
    },
    {
      icon: Clock,
      label: 'Hours Tracked',
      value: financialSummary?.hours_tracked?.toFixed(1) || 0,
      subtext: `£${financialSummary?.average_hourly_rate?.toFixed(0) || 0}/hr avg`,
      color: 'bg-purple-500'
    },
    {
      icon: Briefcase,
      label: 'Active Cases',
      value: cases.filter(c => c.status === 'open' || c.status === 'in_progress').length,
      subtext: `${cases.length} total`,
      color: 'bg-indigo-500'
    }
  ];

  const thisMonth = financialSummary?.this_month_revenue || 0;
  const thisYear = financialSummary?.this_year_revenue || 0;

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-700 rounded-xl shadow-lg p-8 text-white">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-3xl font-serif mb-2">Welcome Back! 👋</h2>
            <p className="text-gray-300 text-lg mb-4">
              Your platform for complete practice management
            </p>
            <div className="flex items-center space-x-6 mt-6">
              <div>
                <p className="text-sm text-gray-400">This Month</p>
                <p className="text-2xl font-bold">£{thisMonth.toLocaleString()}</p>
              </div>
              <div className="h-12 w-px bg-gray-600"></div>
              <div>
                <p className="text-sm text-gray-400">This Year</p>
                <p className="text-2xl font-bold">£{thisYear.toLocaleString()}</p>
              </div>
              <div className="h-12 w-px bg-gray-600"></div>
              <div>
                <p className="text-sm text-gray-400">Pending</p>
                <p className="text-2xl font-bold">£{(financialSummary?.pending_invoices || 0).toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white/10 p-4 rounded-lg">
            <Activity className="h-12 w-12" />
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-1">{stat.label}</p>
              <p className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</p>
              <p className="text-xs text-gray-500">{stat.subtext}</p>
            </div>
          );
        })}
      </div>

      {/* 70/30 Model Breakdown */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <PieChart className="h-5 w-5 mr-2" />
          Revenue Split (70/30 Model)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">Your Share (70%)</span>
              <Zap className="h-5 w-5 text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-blue-600">
              £{(financialSummary?.solicitor_share_70 || 0).toLocaleString()}
            </p>
            <div className="mt-4 bg-blue-100 rounded-full h-3">
              <div className="bg-blue-600 h-3 rounded-full" style={{ width: '70%' }}></div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">Firm Share (30%)</span>
              <Building2 className="h-5 w-5 text-gray-600" />
            </div>
            <p className="text-3xl font-bold text-gray-600">
              £{(financialSummary?.firm_share_30 || 0).toLocaleString()}
            </p>
            <div className="mt-4 bg-gray-200 rounded-full h-3">
              <div className="bg-gray-600 h-3 rounded-full" style={{ width: '30%' }}></div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions & Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Tasks */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Upcoming Tasks</h3>
            <Target className="h-5 w-5 text-gray-400" />
          </div>
          {tasks.slice(0, 5).length > 0 ? (
            <div className="space-y-3">
              {tasks.slice(0, 5).map((task) => (
                <div key={task.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`w-2 h-2 rounded-full ${
                      task.priority === 'urgent' ? 'bg-red-500' :
                      task.priority === 'high' ? 'bg-orange-500' : 'bg-blue-500'
                    }`}></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{task.title}</p>
                      {task.due_date && (
                        <p className="text-xs text-gray-500">
                          Due: {new Date(task.due_date).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    task.status === 'completed' ? 'bg-green-100 text-green-800' :
                    task.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {task.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No tasks yet</p>
          )}
        </div>

        {/* Quick Stats */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Stats</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Paid Invoices</span>
              <span className="text-lg font-bold text-green-600">
                £{(financialSummary?.paid_invoices || 0).toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Pending Invoices</span>
              <span className="text-lg font-bold text-orange-600">
                £{(financialSummary?.pending_invoices || 0).toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Active Clients</span>
              <span className="text-lg font-bold text-blue-600">
                {cases.filter(c => c.status !== 'closed').length}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Import the tab components
import CasesTab from './solicitor-tabs/CasesTab';
import ClientsTab from './solicitor-tabs/ClientsTab';
import TimeTrackingTab from './solicitor-tabs/TimeTrackingTab';
import InvoicesTab from './solicitor-tabs/InvoicesTab';
import TasksTab from './solicitor-tabs/TasksTab';
import DocumentsTab from './solicitor-tabs/DocumentsTab';
import AIAssistant from './solicitor-tabs/AIAssistant';

export default SolicitorDashboard;
